"use client";

import { useRef, useEffect, useState } from "react";
import { useTheme } from "next-themes";
import Image from "next/image";
import {
  AcronymJs,
  AcronymTs,
  LogoOpenAi,
  AcronymHttp,
  Api,
  BrowserChrome,
  Code,
  Database,
  LogoV0,
  LogoVercel,
  Postgres,
  StopwatchFast,
  TerminalWindow,
  Servers,
  FunctionSquare
} from "geist-icons";
import { NextjsIcon } from "@/components/home/about/NextjsIcon";
import { ReactIcon } from "./ReactIcon";
import { GithubIcon } from "./GithubIcon";
import { Skeleton } from "@/components/ui/skeleton";

export default function AnimatedCircularDisplay() {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 700, height: 700 });
  const outerIconRefs = useRef<HTMLDivElement[]>([]);
  const middleIconRefs = useRef<HTMLDivElement[]>([]);
  const innerIconRefs = useRef<HTMLDivElement[]>([]);

  const innerIcons = [
    <AcronymTs key="acronymts" />,
    <LogoVercel key="logovercel" />,
    <ReactIcon key="reacticon" />,
    <NextjsIcon key="nextjsicon" />,
  ];

  const middleIcons = [
    <GithubIcon key="logosanity" />,
    <LogoOpenAi key="logoopenai" />,
    <Postgres key="postgres" />,
    <BrowserChrome key="browserchrome" />,
    <LogoV0 key="logov0" />,
    <AcronymJs key="acronymjs" />,
  ];

  const outerIcons = [
    <TerminalWindow key="terminalwindow" />,
    <Servers key="servers" />,
    <Database key="database" />,
    <FunctionSquare key="functionsquare" />,
    <AcronymHttp key="acronymhttp" />,
    <StopwatchFast key="stopwatchfast" />,
    <Code key="code" />,
    <Api key="api" />,
  ];

  // Animation durations in seconds
  const outerDuration = 80;
  const middleDuration = 50;
  const innerDuration = 30;

  // After mounting, we can safely show the UI that depends on the theme
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let animationFrame: number;
    const start = performance.now();

    const animate = (time: number) => {
      const elapsed = time - start;

      const outerRotation = (elapsed / 1000) * (360 / outerDuration);
      const middleRotation = (elapsed / 1000) * (-360 / middleDuration);
      const innerRotation = (elapsed / 1000) * (360 / innerDuration);

      outerIconRefs.current.forEach((el) => {
        if (!el) return;

        el.style.transform = `rotate(${outerRotation}deg)`;

        const iconEl = el.querySelector(".icon") as HTMLElement;
        if (iconEl) {
          iconEl.style.transform = `rotate(${-outerRotation}deg)`; // block icon rotation
        }
      });

      middleIconRefs.current.forEach((el) => {
        if (!el) return;

        el.style.transform = `rotate(${middleRotation}deg)`;

        const iconEl = el.querySelector(".icon") as HTMLElement;
        if (iconEl) {
          iconEl.style.transform = `rotate(${-middleRotation}deg)`; // block icon rotation
        }
      });

      innerIconRefs.current.forEach((el) => {
        if (!el) return;

        el.style.transform = `rotate(${innerRotation}deg)`;

        const iconEl = el.querySelector(".icon") as HTMLElement;
        if (iconEl) {
          iconEl.style.transform = `rotate(${-innerRotation}deg)`; // block icon rotation
        }
      });

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, []);

  // Calculate orbit radii based on container size
  const scale = size.width / 600;
  const outerRadius = Math.round(295 * scale);
  const middleRadius = Math.round(220 * scale);
  const innerRadius = Math.round(145 * scale);
  const outerCenterRadius = Math.round(60 * scale);
  const innerCenterRadius = Math.round(80 * scale);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas dimensions with device pixel ratio for sharp rendering
    const dpr = window.devicePixelRatio || 1;
    const fixedSize = 800; // o 700, 1100, quello che vuoi tu
    canvas.width = fixedSize * dpr;
    canvas.height = fixedSize * dpr;

    ctx.scale(dpr, dpr);

    const centerX = fixedSize / 2;
    const centerY = fixedSize / 2;

    // Animation variables
    let outerAngle = 0;
    let middleAngle = 0;
    let innerAngle = 0;

    // Animation speeds (degrees per frame)
    const outerSpeed = 360 / (outerDuration * 60); // 60fps
    const middleSpeed = -360 / (middleDuration * 60);
    const innerSpeed = 360 / (innerDuration * 60);

    const drawCircle = (radius: number, dashPattern: number[] | null) => {
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.strokeStyle = isDark ? "#1E1E1E" : "#EAEAEA";
      ctx.lineWidth = 2;

      if (dashPattern) {
        ctx.setLineDash(dashPattern);
      } else {
        ctx.setLineDash([]);
      }

      ctx.stroke();
    };

    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

      // Draw orbit paths
      drawCircle(outerRadius, null); // Dashed
      drawCircle(middleRadius, [10, 15]); // Solid
      drawCircle(innerRadius, null); // More dashed
      drawCircle(outerCenterRadius, [5, 10]); // Solid
      drawCircle(innerCenterRadius, [5, 10]); // Solid

      // Update angles
      outerAngle += outerSpeed;
      middleAngle += middleSpeed;
      innerAngle += innerSpeed;

      requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      // No cleanup needed for canvas animation
    };
  }, [
    isDark,
    outerRadius,
    middleRadius,
    innerRadius,
    outerCenterRadius,
    innerCenterRadius,
  ]);

  // Calculate icon sizes based on container size
  const iconSize = Math.round(56 * scale);
  const iconOffset = iconSize / 2;

  if (!mounted) {
    return (
      <div className="h-full bg-[#FAFAFA] dark:bg-black flex items-center justify-center p-8 relative transition-colors duration-500">
        <div
          className="relative top-30 sm:top-32 md:top-36 aspect-square scale-95 sm:scale-120 md:scale-120 lg:scale-145"
          style={{ width: "800px", height: "800px" }}
        >
          <canvas ref={canvasRef} className="absolute w-full h-full" />
          {/* Optional: placeholder logo to avoid shift */}
          <Skeleton className="relative w-[800px] h-[800px] top-10 sm:top-32 md:top-36 aspect-square scale-95 sm:scale-120 md:scale-120 lg:scale-130 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-[#FAFAFA] dark:bg-black flex items-center justify-center p-8 relative transition-colors duration-500 ">
      <div
        ref={containerRef}
        className="relative top-30 sm:top-32 md:top-36 aspect-square scale-95 sm:scale-120 md:scale-120 lg:scale-145"
        style={{ width: "800px", height: "800px" }}
      >
        {/* Canvas for smooth animation */}
        <canvas ref={canvasRef} className="absolute w-full h-full" />

        {/* Render React icons on top of canvas circles */}
        <div className="relative w-full h-full">
          {/* Outer orbit icons */}
          {outerIcons.map((icon, index) => {
            const angle = (index * 2 * Math.PI) / outerIcons.length;
            const x = Math.cos(angle) * outerRadius;
            const y = Math.sin(angle) * outerRadius;

            return (
              <div
                ref={(el) => {
                  if (el) outerIconRefs.current[index] = el;
                }}
                key={`outer-${index}`}
                className="absolute flex items-center justify-center rounded-full border-2 border-[#EAEAEA] dark:border-[#1E1E1E]  bg-white dark:bg-[#0A0A0A]"
                style={{
                  left: `calc(50% + ${x}px - ${iconOffset}px)`,
                  top: `calc(50% + ${y}px - ${iconOffset}px)`,
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  transformOrigin: `${iconOffset - x}px ${iconOffset - y}px`,
                }}
              >
                <div className="icon text-[#0A0A0A] dark:text-white scale-110">
                  {icon}
                </div>
              </div>
            );
          })}
          {/* Middle orbit icons */}
          {middleIcons.map((icon, index) => {
            const angle = (index * 2 * Math.PI) / middleIcons.length;
            const x = Math.cos(angle) * middleRadius;
            const y = Math.sin(angle) * middleRadius;

            return (
              <div
                ref={(el) => {
                  if (el) middleIconRefs.current[index] = el;
                }}
                key={`middle-${index}`}
                className="absolute flex items-center justify-center rounded-full border-2 border-[#EAEAEA] dark:border-[#1E1E1E] bg-white dark:bg-[#0A0A0A]"
                style={{
                  left: `calc(50% + ${x}px - ${iconOffset}px)`,
                  top: `calc(50% + ${y}px - ${iconOffset}px)`,
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  transformOrigin: `${iconOffset - x}px ${iconOffset - y}px`,
                }}
              >
                <div className="icon text-[#0A0A0A] dark:text-white scale-110">
                  {icon}
                </div>
              </div>
            );
          })}
          {/* Inner orbit icons */}
          {innerIcons.map((icon, index) => {
            const angle = (index * 2 * Math.PI) / innerIcons.length;
            const x = Math.cos(angle) * innerRadius;
            const y = Math.sin(angle) * innerRadius;

            return (
              <div
                ref={(el) => {
                  if (el) innerIconRefs.current[index] = el;
                }}
                key={`inner-${index}`}
                className="absolute flex items-center justify-center rounded-full border-2 border-[#EAEAEA] dark:border-[#1E1E1E] bg-white dark:bg-[#0A0A0A]"
                style={{
                  left: `calc(50% + ${x}px - ${iconOffset}px)`,
                  top: `calc(50% + ${y}px - ${iconOffset}px)`,
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  transformOrigin: `${iconOffset - x}px ${iconOffset - y}px`,
                }}
              >
                <div className="icon text-[#0A0A0A] dark:text-white scale-110">
                  {icon}
                </div>
              </div>
            );
          })}
          {/* Central logo */}
          {mounted ? (
            <Image
              src={isDark ? "/my-logo-white.svg" : "/my-logo-black.svg"}
              alt="logo"
              width={25}
              height={25}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-18 h-18 md:w-20 md:h-20 flex items-center justify-center"
            />
          ) : (
            // Placeholder to prevent layout shift during hydration
            <Skeleton className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-18 h-18 md:w-20 md:h-20 flex items-center justify-center rounded-full" />
          )}{" "}
        </div>
      </div>
    </div>
  );
}
